import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../../../../../services/api';
import { useToast } from '../../../../../../hooks/toast';

interface OAuthConnectionMonitorProps {
  isOpen: boolean;
  onConnectionSuccess: () => void;
  connectionState: string;
}

interface OAuthStatusResponse {
  id: number;
  state: string;
  account_id: number;
  error: string | null;
  error_description: string | null;
  hint: string | null;
  message: string | null;
  completed_processing: boolean;
}

type ConnectionStatus = 'pending' | 'success' | 'failed';

const MAX_RETRIES = 60;
const POLLING_INTERVAL = 1000;

const OAuthConnectionMonitor: React.FC<OAuthConnectionMonitorProps> = ({
  isOpen,
  onConnectionSuccess,
  connectionState,
}) => {
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('pending');
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const retryCount = useRef(0);
  const { addToast } = useToast();

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const handleFailure = useCallback(
    (title: string, description: string) => {
      stopPolling();
      setConnectionStatus('failed');
      addToast({ type: 'error', title, description });
    },
    [stopPolling, addToast],
  );

  const checkConnectionStatus = useCallback(async () => {
    if (!connectionState) return;

    try {
      const { data } = await api.get<OAuthStatusResponse>(
        '/admin/beersales/check-authorization-status',
        { params: { state: connectionState } },
      );

      const { completed_processing, error, error_description, message } = data;

      if (completed_processing) {
        stopPolling();

        if (error) {
          handleFailure(
            'Erro na conexão',
            error_description ||
              message ||
              `Não foi possível estabelecer conexão com a API BeerSales. (${error})`,
          );
        } else {
          setConnectionStatus('success');
          onConnectionSuccess();
          addToast({
            type: 'success',
            title: 'Sucesso!',
            description:
              'Conexão com a API BeerSales estabelecida com sucesso!',
          });
        }
        return;
      }

      // eslint-disable-next-line no-plusplus
      if (++retryCount.current >= MAX_RETRIES) {
        handleFailure(
          'Tempo limite excedido',
          'O processo de autorização demorou muito tempo. Por favor, tente novamente.',
        );
      }
    } catch (err) {
      console.error('Erro ao verificar status da conexão OAuth:', err);
      // eslint-disable-next-line no-plusplus
      if (++retryCount.current >= MAX_RETRIES) {
        handleFailure(
          'Erro na conexão',
          'Não foi possível verificar o status da conexão com a API BeerSales.',
        );
      }
    }
  }, [
    connectionState,
    stopPolling,
    handleFailure,
    addToast,
    onConnectionSuccess,
  ]);

  const startPolling = useCallback(() => {
    retryCount.current = 0;
    checkConnectionStatus();
    pollingRef.current = setInterval(checkConnectionStatus, POLLING_INTERVAL);
  }, [checkConnectionStatus]);

  useEffect(() => {
    if (isOpen && connectionStatus === 'pending' && connectionState) {
      startPolling();
    }
    return stopPolling;
  }, [isOpen, connectionStatus, connectionState, startPolling, stopPolling]);

  useEffect(() => {
    if (isOpen) setConnectionStatus('pending');
  }, [isOpen]);

  return null;
};

export default OAuthConnectionMonitor;
